/**
 * Data & Privacy settings page — /office/settings/data-privacy/
 *
 * Five concerns, all previously inline in ``data_privacy.html`` (157
 * LOC + 1 ``onchange=``), now consolidated into a single ESM module
 * that auto-inits via ``data-auto-init="dataPrivacy"`` on the page
 * wrapper. CSP-clean (no inline scripts), HTMX-safe (idempotency
 * guards + per-node ``_dpBound`` flags so an hx-boost re-mount does
 * not double-bind).
 *
 *   1. Generic eager-clear + submit-time validation for forms tagged
 *      ``data-validate-select`` (the SAR forms and the anonymize form
 *      use it). Mirrors the "hybrid pro pattern" — backend remains the
 *      single source of truth, frontend only paints inline ``.field-error``
 *      messages under each ``.field-group`` to avoid double-toast noise.
 *
 *   2. Anonymize-customer confirmation : the Submit button is gated
 *      until the typed name matches the selected customer, and a
 *      ``showConfirmModal`` (already global) is opened on submit.
 *
 *   3. Delete-organization confirmation : same pattern as (2) but
 *      against the literal organization name from
 *      ``json_script#data-privacy-config`` (escapejs-safe).
 *
 *   4. Spinner injection on the three export buttons (org-wide /
 *      customer SAR / employee SAR) so the UI does not look frozen
 *      while the backend kicks off the Celery export task.
 *
 *   5. Confirm modals route through ``window.showConfirmModal`` (no
 *      window.alert / window.confirm fallback — the global is
 *      always present in office layouts since Phase 6).
 *
 * No new globals are exposed: the legacy
 * ``onchange="updateAnonymizeConfirmation()"`` attribute is dropped
 * because we bind the change listener programmatically inside
 * ``initAnonymizeFlow``.
 */

function readJsonScript(id, fallback) {
  var node = document.getElementById(id);
  if (!node) return fallback;
  try {
    return JSON.parse(node.textContent);
  } catch (e) { // eslint-disable-line no-unused-vars
    return fallback;
  }
}

// ────────────────────────────────────────────────────────────────────
// 1) Generic form validation : data-validate-select
// ────────────────────────────────────────────────────────────────────

function showFieldError(fieldGroup, field, msg) {
  fieldGroup.classList.add('has-error');
  field.classList.add('border-red-500', 'dark:border-red-500');
  field.classList.remove('border-gray-300', 'dark:border-gray-600');
  if (!fieldGroup.querySelector('.field-error')) {
    var p = document.createElement('p');
    p.className = 'mt-1 text-sm text-red-600 dark:text-red-500 field-error';
    p.textContent = msg;
    field.parentNode.appendChild(p);
  }
}

function clearFieldError(fieldGroup) {
  fieldGroup.classList.remove('has-error');
  var field = fieldGroup.querySelector('select, input');
  if (field) {
    field.classList.remove('border-red-500', 'dark:border-red-500');
  }
  var err = fieldGroup.querySelector('.field-error');
  if (err) err.remove();
}

function initFormValidation(root, i18n) {
  var defaultRequiredMsg = i18n.requiredFieldMsg || 'This field is required.';
  root.querySelectorAll('form[data-validate-select]').forEach(function (form) {
    if (form._dpValidateBound) return;
    form._dpValidateBound = true;

    var selects = form.querySelectorAll('select[required]');
    var inputs = form.querySelectorAll('input[required]');

    selects.forEach(function (sel) {
      sel.addEventListener('change', function () {
        var fg = sel.closest('.field-group');
        if (fg) clearFieldError(fg);
      });
    });

    inputs.forEach(function (inp) {
      inp.addEventListener('input', function () {
        var fg = inp.closest('.field-group');
        if (fg) clearFieldError(fg);
      });
    });

    form.addEventListener('submit', function (e) {
      var hasError = false;

      selects.forEach(function (sel) {
        if (!sel.value) {
          var fg = sel.closest('.field-group');
          if (fg) showFieldError(fg, sel, sel.dataset.errorMsg || defaultRequiredMsg);
          hasError = true;
        }
      });

      inputs.forEach(function (inp) {
        if (!inp.value.trim()) {
          var fg = inp.closest('.field-group');
          if (fg) showFieldError(fg, inp, inp.dataset.errorMsg || defaultRequiredMsg);
          hasError = true;
        }
      });

      if (hasError) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    });
  });
}

// ────────────────────────────────────────────────────────────────────
// 2) Anonymize-customer flow
// ────────────────────────────────────────────────────────────────────

function initAnonymizeFlow(root, i18n) {
  var aSelect = root.querySelector('#anonymizeCustomerSelect');
  var aInput = root.querySelector('#anonymizeConfirmInput');
  var aBtn = root.querySelector('#anonymizeBtn');
  var aHint = root.querySelector('#anonymizeHint');
  if (!aSelect || !aInput || !aBtn || !aHint) return;

  var placeholder = i18n.selectCustomerPlaceholder || 'Select a customer...';
  var msgNoMatch = i18n.nameNoMatch || 'Name does not match.';
  var msgMatch = i18n.nameMatch || 'Name matches \u2014 ready to submit.';

  function selectedName() {
    var opt = aSelect.options[aSelect.selectedIndex];
    return opt ? opt.text : '';
  }

  function checkMatch() {
    var name = selectedName();
    var val = aInput.value.trim();
    var match = val === name && name !== '' && name !== placeholder;
    aBtn.disabled = !match;
    aBtn.classList.toggle('opacity-50', !match);

    if (!val) {
      aHint.classList.add('hidden');
    } else if (match) {
      aHint.textContent = msgMatch;
      aHint.className = 'mt-1 text-sm text-green-600 dark:text-green-400';
    } else {
      aHint.textContent = msgNoMatch;
      aHint.className = 'mt-1 text-sm text-red-600 dark:text-red-500';
    }
  }

  function refreshPlaceholder() {
    var name = selectedName();
    aInput.placeholder = (name && name !== placeholder) ? name : '...';
    checkMatch();
  }

  if (!aInput._dpInputBound) {
    aInput._dpInputBound = true;
    aInput.addEventListener('input', checkMatch);
  }
  if (!aSelect._dpChangeBound) {
    aSelect._dpChangeBound = true;
    aSelect.addEventListener('change', refreshPlaceholder);
  }

  var form = aBtn.closest('form');
  if (form && !form._dpAnonSubmitBound) {
    form._dpAnonSubmitBound = true;
    form.addEventListener('submit', function (e) {
      if (aBtn.disabled) return;
      e.preventDefault();
      if (typeof window.showConfirmModal !== 'function') {
        form.submit();
        return;
      }
      window.showConfirmModal({
        title: i18n.anonymizeTitle || 'Anonymize Customer',
        message: i18n.anonymizeMessage || 'Are you absolutely sure? This cannot be undone.',
        confirmText: i18n.anonymizeConfirm || 'Anonymize',
        type: 'danger',
        onConfirm: function () {
          addSpinner(aBtn);
          form.submit();
        },
      });
    });
  }
}

// ────────────────────────────────────────────────────────────────────
// 3) Delete-organization flow
// ────────────────────────────────────────────────────────────────────

function initDeleteOrgFlow(root, config, i18n) {
  var dInput = root.querySelector('#deleteOrgConfirmInput');
  var dBtn = root.querySelector('#deleteOrgBtn');
  var dHint = root.querySelector('#deleteOrgHint');
  if (!dInput || !dBtn) return;

  var orgName = config.orgName || '';
  var msgNoMatch = i18n.nameNoMatch || 'Name does not match.';
  var msgMatch = i18n.nameMatch || 'Name matches \u2014 ready to submit.';

  if (!dInput._dpInputBound) {
    dInput._dpInputBound = true;
    dInput.addEventListener('input', function () {
      var val = dInput.value.trim();
      var match = val === orgName;
      dBtn.disabled = !match;
      dBtn.classList.toggle('opacity-50', !match);

      if (!dHint) return;
      if (!val) {
        dHint.classList.add('hidden');
      } else if (match) {
        dHint.textContent = msgMatch;
        dHint.className = 'mt-1 text-sm text-green-600 dark:text-green-400';
      } else {
        dHint.textContent = msgNoMatch;
        dHint.className = 'mt-1 text-sm text-red-600 dark:text-red-500';
      }
    });
  }

  var form = dBtn.closest('form');
  if (form && !form._dpDelSubmitBound) {
    form._dpDelSubmitBound = true;
    form.addEventListener('submit', function (e) {
      if (dBtn.disabled) return;
      e.preventDefault();
      if (typeof window.showConfirmModal !== 'function') {
        form.submit();
        return;
      }
      window.showConfirmModal({
        title: i18n.deleteOrgTitle || 'Delete Organization',
        message: i18n.deleteOrgMessage || 'This will deactivate your organization immediately. Are you sure?',
        confirmText: i18n.deleteOrgConfirm || 'Delete',
        type: 'danger',
        onConfirm: function () {
          addSpinner(dBtn);
          form.submit();
        },
      });
    });
  }
}

// ────────────────────────────────────────────────────────────────────
// 4) Spinner-on-submit for the three export buttons
// ────────────────────────────────────────────────────────────────────

var SPINNER_SVG = '<svg class="animate-spin h-4 w-4 mr-2 text-white inline" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';

function addSpinner(btn) {
  if (!btn || btn.disabled) return;
  btn.disabled = true;
  btn.innerHTML = SPINNER_SVG + btn.textContent.trim();
}

function initExportSpinners(root) {
  ['export-all-btn', 'export-customer-btn', 'export-employee-btn'].forEach(function (id) {
    var btn = root.querySelector('#' + id);
    if (!btn) return;
    var form = btn.closest('form');
    if (!form || form._dpSpinnerBound) return;
    form._dpSpinnerBound = true;
    form.addEventListener('submit', function () { addSpinner(btn); });
  });
}

// ────────────────────────────────────────────────────────────────────
// MAIN INIT
// ────────────────────────────────────────────────────────────────────

export function initDataPrivacy(options) {
  options = options || {};
  var root = options.root || document.querySelector('[data-auto-init="dataPrivacy"]');
  if (!root) return;

  var config = readJsonScript('data-privacy-config', {});
  var i18n = config.i18n || {};

  initFormValidation(root, i18n);
  initAnonymizeFlow(root, i18n);
  initDeleteOrgFlow(root, config, i18n);
  initExportSpinners(root);
}

export function autoInitDataPrivacy() {
  var root = document.querySelector('[data-auto-init="dataPrivacy"]');
  if (!root) return;
  initDataPrivacy({ root: root });
}
